import { ReactNode } from 'react';

import { DonutSlice, LegendItem } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { toUsFormat } from 'utils/utils';
import type { CapacityPool } from 'features/common/hooks/useEventCapacity';
import {
	COLOR_AVAIL,
	COLOR_OVERSOLD,
	COLOR_POOL_BLOCKED,
	buildFillRingData,
	computePoolRevenueCeiling,
	formatCompactRevenue,
	paidCount,
} from './inventoryUtils';
import { POOL_OVERSUBSCRIBED_MESSAGE } from 'features/common/utils/poolCopy';
import type { InventoryColumn } from './useTicketInventoryData';
import type { InventoryColumnCardProps } from './InventoryColumnCard';
import type { ColumnPalette } from './buildTypeColumnProps';

// Ceiling ring: every dollar slice formats as $, but the lone "No ceiling" placeholder (a pool with no priced
// tiers) has no dollar meaning — show it as "—" so an empty pool no longer reads "$1.00" on hover.
const ceilingTooltipFormatter = (value: number, name: string): [string, string] =>
	name === 'No ceiling' ? ['—', name] : [toUsFormat(value), name];

/**
 * Builds a card for one shared-capacity pool, rendered in the "Ticket Pool Inventory" section. It reuses
 * the same `InventoryColumnCard` shell as the ticket-type cards (tile → donut → ring) so the two sections
 * scale identically:
 *
 *   tile         — pool name + estimated revenue received from the pool's sales (Σ member paid × price)
 *   composition  — per-tier SOLD (paid) + a single COMPED slice + the shared remainder; the base is the
 *                  pool's shared capacity, EXCEPT when over-subscribed (issued > capacity) the base becomes
 *                  the sum of the parts so the slices fill the full ring. Center = issued (sold + comped) ÷
 *                  capacity, highlighted red when over capacity. Derived from member counts, not the
 *                  unstable pool.sold scalar — docs/pools_business_logic.md §3.
 *   ceiling ring — the most the pool could earn (greedy: priciest tiers first, up to shared capacity)
 */
export function buildPoolColumnProps(
	pool: CapacityPool,
	columns: InventoryColumn[],
	palette: ColumnPalette,
	t: (key: string) => string
): InventoryColumnCardProps {
	const { ticketTypeColors, poolColors, onHoldColor, compedColor } = palette;
	const poolColor = poolColors[pool.name] ?? onHoldColor;

	const memberSet = new Set(pool.memberNames);
	const members = columns.filter((c) => memberSet.has(c.name));

	// Pool usage comes straight from the shared useEventCapacity source (Lifetime-Σ: pool.sold = Σ member
	// issued, NOT the stale-low backend pool.sold scalar), so the dashboard tile, the Send tiles, and this
	// card agree by construction. `sellableRemaining` is the shared open capacity members can actually draw;
	// `slack` is capacity the pool holds beyond any tier's size (it can never sell).
	const overSubscribed = pool.sold > pool.totalAmount;
	const totalComped = members.reduce((s, c) => s + c.compedCount, 0);

	// ── Composition donut ──────────────────────────────────────────────────────
	// Per-tier SOLD (paid, comps excluded) + one aggregate Comped slice + the shared remainder, SPLIT into
	// what tiers can still sell (Available) and the pool's slack beyond tier capacity (Pool empty) so the
	// genuine Available is never hidden behind an oversized pool. Comps DO consume pool capacity (an issued
	// seat is a seat taken), so they sit inside the ring. When over-subscribed both remainders are 0, so the
	// parts fill the full ring (base = Σ parts, not cap).
	const memberSlices: DonutSlice[] = members
		.map((c) => ({ name: c.name, paid: paidCount(c.issued, c.compedCount) }))
		.filter((m) => m.paid > 0)
		.map((m) => ({ label: m.name, value: m.paid, color: ticketTypeColors[m.name] ?? COLOR_AVAIL }));

	const compositionData: DonutSlice[] = [
		...memberSlices,
		totalComped > 0 ? { label: t('analytics-inventory-comped'), value: totalComped, color: compedColor } : null,
		pool.sellableRemaining > 0
			? { label: t('analytics-inventory-available'), value: pool.sellableRemaining, color: COLOR_AVAIL }
			: null,
		pool.slack > 0
			? { label: t('analytics-inventory-pool-empty'), value: pool.slack, color: COLOR_POOL_BLOCKED }
			: null,
	].filter(Boolean) as DonutSlice[];

	const compositionLegend: LegendItem[] = [
		...memberSlices.map((s) => ({ label: s.label, color: s.color })),
		...(totalComped > 0 ? [{ label: t('analytics-inventory-comped'), color: compedColor }] : []),
		...(pool.sellableRemaining > 0 ? [{ label: t('analytics-inventory-available'), color: COLOR_AVAIL }] : []),
		...(pool.slack > 0 ? [{ label: t('analytics-inventory-pool-empty'), color: COLOR_POOL_BLOCKED }] : []),
	];

	// ── Revenue tile + ceiling ring ──────────────────────────────────────────────
	// Ceiling = revenue received + the most still achievable from the pool's REMAINING shared capacity
	// (greedy, priciest member tiers first, each capped by its pool-capped `available`). A fully-sold pool
	// (usage.remaining 0) → ceiling = received → the ring reads 100%, matching the pool-capped per-type rings.
	const poolRevenue = members.reduce((s, c) => s + c.payout, 0);
	const ceiling = computePoolRevenueCeiling(poolRevenue, members, pool.poolRemaining);

	const tileHelp: ReactNode = (
		<PunchupHelpButton
			mainText={`"${pool.name}" is a shared pool — its member tiers draw from one capacity of ${pool.totalAmount}. The figure is the estimated revenue received from this pool's sales (paid tickets × price).`}
			codeText={`shared capacity = ${pool.totalAmount}\nout (sold + comped) = ${pool.sold}\nleft = ${pool.poolRemaining}`}
			secondaryText="A tier in a pool stops selling when either its own quantity or the shared pool runs out — whichever comes first."
		/>
	);

	const compositionHelp: ReactNode = (
		<PunchupHelpButton
			mainText={`How the "${pool.name}" pool's shared capacity is split across its member tiers.`}
			codeText={[
				"each colour = a tier's sold (paid) count",
				'comped = tickets given away (they still use a shared seat)',
				'light grey = shared capacity the tiers can still sell',
				...(pool.slack > 0 ? ["mid grey = pool capacity beyond any tier's size (can't sell)"] : []),
			].join('\n')}
			secondaryText={
				overSubscribed
					? POOL_OVERSUBSCRIBED_MESSAGE
					: pool.slack > 0
					? 'The pool is bigger than its tiers combined, so some of its capacity ("Pool empty") can never be sold — add tier capacity or shrink the pool.'
					: 'Sold and comped both count against the shared capacity; the grey slice is what is still open.'
			}
		/>
	);

	const ceilingHelp: ReactNode = (
		<PunchupHelpButton
			mainText={`The most the "${pool.name}" pool could earn — its highest-priced tiers selling first, up to the shared capacity.`}
			codeText={`received = ${toUsFormat(poolRevenue)}\nceiling = ${toUsFormat(ceiling)}`}
			secondaryText="The shared capacity caps how many tickets can sell, so selling the priciest tiers first sets the most a pool can make."
		/>
	);

	return {
		tile: {
			title: pool.name,
			value: formatCompactRevenue(poolRevenue),
			descriptions: [`${pool.poolRemaining} ${t('analytics-inventory-pool-left')}`],
			helpButton: tileHelp,
		},
		issuedDonut: {
			centerLabel: `${pool.sold}/${pool.totalAmount}`,
			centerLabelColor: overSubscribed ? COLOR_OVERSOLD : undefined,
			centerTooltip: overSubscribed
				? `${pool.sold} tickets are out across this pool's tiers, but its shared capacity is only ${pool.totalAmount}. This usually means some tickets were issued before the pool was created.`
				: `${pool.sold} of ${pool.totalAmount} shared seats used (${pool.poolRemaining} still open).`,
			data:
				compositionData.length > 0
					? compositionData
					: [{ label: t('analytics-inventory-available'), value: 1, color: COLOR_AVAIL }],
			legendItems: compositionLegend,
			helpButton: compositionHelp,
		},
		fillDonut: {
			// Center is the revenue RECEIVED (so an unsold pool reads $0), matching the per-type fill rings;
			// the ceiling is the ring's full value + the tooltip, not the center.
			centerLabel: formatCompactRevenue(poolRevenue),
			centerTooltip:
				ceiling > 0
					? `${toUsFormat(poolRevenue)} received of ${toUsFormat(ceiling)} maximum`
					: 'No priced tiers in this pool — no revenue ceiling.',
			data: buildFillRingData(poolRevenue, ceiling, poolColor),
			tooltipFormatter: ceilingTooltipFormatter,
			helpButton: ceilingHelp,
		},
	};
}
